import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Trash2, X } from 'lucide-react';
import {
  useAddNoteMutation,
  useDeleteNoteMutation,
} from '../../hooks/useAdAnnotations';
import type { AdAnnotation, AdNote } from '../../api/adAnnotations';

interface NotesCellProps {
  accountId: string;
  adId: string;
  adName: string;
  annotation?: AdAnnotation;
}

export default function NotesCell({
  accountId,
  adId,
  adName,
  annotation,
}: NotesCellProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const notes: AdNote[] = annotation?.notes ?? [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
      >
        <MessageSquare className="h-3.5 w-3.5" />
        {notes.length > 0 ? (
          <span className="font-medium text-gray-800">
            {t('insights.noteCount', { count: notes.length })}
          </span>
        ) : (
          <span className="text-gray-400">{t('insights.addNote')}</span>
        )}
      </button>
      {open && (
        <NotesModal
          accountId={accountId}
          adId={adId}
          adName={adName}
          annotation={annotation}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

interface NotesModalProps {
  accountId: string;
  adId: string;
  adName: string;
  annotation?: AdAnnotation;
  onClose: () => void;
}

function NotesModal({ accountId, adId, adName, annotation, onClose }: NotesModalProps) {
  const { t, i18n } = useTranslation();
  const [text, setText] = useState('');
  const addNoteMut = useAddNoteMutation();
  const deleteNoteMut = useDeleteNoteMutation();
  const notes = [...(annotation?.notes ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  function submit() {
    const trimmed = text.trim();
    if (!trimmed) return;
    addNoteMut.mutate(
      { accountId, adId, text: trimmed },
      { onSuccess: () => setText('') }
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b p-4">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-semibold text-gray-900">
              {t('insights.notes')}
            </h3>
            <p className="mt-0.5 truncate text-xs text-gray-500">{adName}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {notes.length === 0 ? (
            <p className="text-center text-sm text-gray-400">{t('insights.notesEmpty')}</p>
          ) : (
            <ul className="space-y-3">
              {notes.map((note) => (
                <li
                  key={note._id}
                  className="rounded-md border border-gray-200 bg-gray-50 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="whitespace-pre-wrap break-words text-sm text-gray-800">
                      {note.text}
                    </p>
                    {annotation && (
                      <button
                        onClick={() => {
                          if (window.confirm(t('insights.deleteNoteConfirm'))) {
                            deleteNoteMut.mutate({
                              annotationId: annotation._id,
                              noteId: note._id,
                            });
                          }
                        }}
                        className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        aria-label={t('common.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-400">
                    {new Date(note.createdAt).toLocaleString(i18n.language)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t p-4">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder={t('insights.addNotePlaceholder')}
            className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
          />
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('common.close')}
            </button>
            <button
              onClick={submit}
              disabled={!text.trim() || addNoteMut.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {addNoteMut.isPending ? t('common.loading') : t('insights.addNote')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
